/* eslint-disable @khanacademy/ts-no-error-suppressions */
/* eslint-disable react/no-unsafe */
/**
 * Renders a fullscreen modal.
 *
 * Use ModalNavigator when navigation between modals or opening a modal from
 * another modal is required, see modal-navigator.jsx.
 *
 * Usage:
 *      <Modal onClose={this.handleClose}>
 *          <ModalHeader> Title </ModalHeader>
 *          Lorem ipsum dolor...
 *          <ModalFooter style={{justifyContent:'space-between'}}>
 *              <Button>Back</Button>
 *              <Button>Next</Button>
 *          </ModalFooter>
 *      </Modal>
 *
 * TODO(kevinb):
 * - Figure out how to set aria-labelledby since we can have multiple modals in
 *   the render tree at the same time.
 * - Switch to an inline style for 'style' prop and use TypeScript type to constrain
 *   which properties are allowed.
 * - Extract previous/next navigation into a wrapper component, maybe.
 * - Simplify animations using ReactMotion.
 * - Make hostInBody the default and deprecate prop.  Be careful to handle there
 *   being multiple modals on the screen at the same time.
 */

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import PropTypes from "prop-types";
import * as React from "react";
import {createPortal} from "react-dom";

import {Id} from "@khanacademy/wonder-blocks-core";
import {
    ActionSchedulerProvider,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import {color} from "@khanacademy/wonder-blocks-tokens";
import Log, {Errors} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";
import {
    baseBorderRadius,
    mediaQueries,
    topBarHeightDesktop,
    topBarHeightMobile,
    zindexAboveModal,
    zindexModal,
} from "@khan/shared-styles";

import BackIcon from "#reusable-components-package/back-icon.tsx";
import CloseIcon from "#reusable-components-package/close-icon.tsx";
import invariant from "#shared-package/invariant.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedIcon from "#shared-styles-package/deprecated-icon.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {deprecatedTypography} from "#shared-styles-package/deprecated-typography.ts";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedControlButton, {
    type controlButtonColorsType,
} from "../button-package/deprecated-control-button.tsx";

import Backdrop from "./backdrop.tsx";
import ModalFooter from "./modal-footer.tsx";
import ModalHeader from "./modal-header.tsx";
import ScrollDisabler from "./scroll-disabler.ts";
import type {Navigator} from "./types.ts";

// sync-start:triggerless-modal-focus-anchor 1705712473 apps/khanacademy/src/page-package/util.ts
const TRIGGLESS_MODAL_FOCUS_ANCHOR_ID = "triggerless-modal-focus-anchor";
// sync-end:triggerless-modal-focus-anchor

// sync-start:modal-parent-id 888868076 ./apps/khanacademy/src/site-banners/banners/accept-tos/accept-tos.tsx
const MODAL_PARENT_ID = "__MODAL_PARENT__";
// sync-end:modal-parent-id

// sync-start:tos-modal-id 477367603 ./apps/khanacademy/src/site-banners/banners/accept-tos/accept-tos.tsx
const TOS_MODAL_ID = "accept-tos-modal";
// sync-end:tos-modal-id

const MAX_FOCUS_ATTEMPT_NUMBER = 10;

function getModalParent(): HTMLElement {
    // @ts-expect-error - TS2322 - Type 'Element | null' is not assignable to type 'HTMLElement | HTMLDivElement | null | undefined'.
    let modalParent: HTMLElement | HTMLDivElement | null | undefined =
        // TODO(somewhatabstract): Note that if the id contains :s like useId
        // IDs, this will need escaping before calling querySelector.
        document.querySelector(`#${MODAL_PARENT_ID}`);

    if (modalParent) {
        return modalParent;
    }

    modalParent = document.body?.appendChild(document.createElement("div"));
    invariant(modalParent);
    modalParent.id = MODAL_PARENT_ID;

    // The AcceptTosBanner modal needs to appear on top of other modals.
    // It uses the Wonder Blocks modal, which has the same z-index as this modal.
    // In order to stop this modal from sitting on top of it, for now, let's check
    // for that modal first and hide this modal if needed.
    // The AcceptTosBanner updates the display style on this element before
    // closing or unmounting.
    const maybeAcceptTosBanner = document.getElementById(TOS_MODAL_ID);
    if (maybeAcceptTosBanner) {
        modalParent.style.display = "none";
    }

    return modalParent;
}

type Style = any;

type DefaultProps = {
    // Determines whether the modal should animate when it's added/removed from
    // the render tree.
    shouldAnimate: boolean;
    // Controls whether or not tapping the backdrop should dismiss the modal.
    shouldBackdropDismiss: () => boolean;
    // Determines whether the modal is currently transitioning from one modal
    // component to the next
    fromAnotherModal?: boolean;
    // Whether or not the modal is visible.  Used when for stacked modal
    // navigation to maintain the stack of modals lower in the stack, see
    // modal-navigator.jsx.
    // > NOTE: This prop shouldn't be used to toggle the visibility of a single
    // modal; you'll run into scroll-locking issues if you try.
    visible?: boolean;
    // Controls whether the modal should close when the user tries to close it.
    // Returning false prevents the modal from closing allowing the parent to
    // show a confirmation dialog.
    onRequestClose: () => boolean | null | undefined;
    // Tell the parent to close the modal.
    onClose: () => unknown;
    // Prevents the modal from being dismissed by clicking on the close button,
    // tapping on the backdrop, or hitting the ESC key.
    preventDismissal?: boolean;
    // Hides the "X" dismissal icon, without interfering with other dismissal
    // behaviors (tapping on the backdrop, or hitting the ESC key).
    hideCloseIcon?: boolean;
    // Should this modal ensure that focus always remains within the modal, or
    // is it alright for things (eg. a toolbar above the modal) to take focus?
    restrictFocusToModal: boolean;
};

// TODO(kevinb): extract common types into types.js so that they can be shared
// with other modals.  This requires the spread operator inside an exact object
// types which our current version of babel doesn't support.  see
// https://github.com/yannickcr/eslint-plugin-react/issues/1178
type Props = DefaultProps & {
    // Should the modal have role="alertdialog"
    alert?: boolean;
    // Content to go in the modal body.
    //
    // Usually this will be a plain React node. However, if you'd like to
    // trigger modal-closing from inside your modal's contents, you can instead
    // provide a function that accepts a `closeModal` function as an argument,
    // and returns a React node. In that case, we'll call the function, and
    // render the returned React node.
    //
    // For example:
    //      children={({closeModal}) =>
    //          Hello, world!<br />
    //          <button onClick={closeModal>Close me!</button>
    //     }
    children?:
        | React.ReactNode
        | ((arg1: {closeModal: () => void}) => React.ReactNode);
    // Close a modal that was opened from with a modal.  This happens when
    // ExerciseModal opens a related video in a VideoModal, see
    // modal-navigator.jsx.
    onBack?: () => unknown;
    // aphrodite style or array of aphrodite styles TODO(kevinb): switch to an
    // inline style and use TypeScript type to constrain which properties are allowed.
    style?: Style | Array<Style>;
    // Element or array of elements to render in the right of the titlebar.
    topRightElements?: React.ReactNode;
    // Change the close icon to be the back icon.  Used when for stacked modal
    // navigation, see modal-navigator.jsx
    useBackIcon?: boolean;
    // Navigation delegate containing callbacks for onNext, onPrev, hasNext, and
    // hasPrev.
    navigator?: Navigator;
    // Used mostly for test fixtures. Lets the modal get hosted in a container.
    inline?: boolean;
    // Certain modals might have a top bar (eg. the assignment header).  By
    // providing it here, the modal will account for it and space itself
    // accordingly.
    topBar?: React.ReactNode;
    // Custom colors for the "X" or "<-" button.
    controlButtonColors?: controlButtonColorsType;
    // When true, this property tells the modal to parent in document.body.
    // This can help with situations where it doesn't render properly directly
    // in a document tree for whatever reason.
    hostInBody?: boolean;
} & WithActionSchedulerProps;

type State = {
    // The willBeVisible and isModalOpen flags are being used to animate the
    // modal and backdrop in and out of view.  We're not using
    // AphroditeCSSTransitionGroup because it doesn't support styling of the
    // group itself which is necessary because this layout is using flexbox.

    // true if the modal will be visible at the end of the current animation.
    willBeVisible: boolean;
    // true when the modal has finished animating into its open position, false
    // otherwise.
    isModalOpen: boolean;
    // This flag controls whether the CSS transition rules are applied.  On
    // mount, this flag is false. After mount, this flag becomes true.  This
    // ensures that the transition rules are not applied until we've correctly
    // initialized the element's CSS, because browsers aren't consistent about
    // what happens when you apply them at the same time.
    //
    // For example, at some call sites (but not all), Firefox will mount the
    // elements as visible, then animate them to opacity 0, then animate them
    // back once the animation timeouts resolve. It's not clear why this
    // behavior was inconsistent, but this addition definitely resolves it!
    startAnimations: boolean;
    // When a modal navigates to another modal, fromAnotherModal is true for the
    // new modal and animation styles are not applied. However, we still want
    // closing animations to be applied if showAnimations is true. Redux knows
    // when a user closes the modal group, but redux takes the latest modalData
    // off the stack and there is no chance to rerender the modal from there.
    forceClosingAnimation: boolean;
    // The modal container element.  Used in context for components further down
    // the child hiearchy to know where the new visual root is.  This lives in
    // state so that it will re-render the children and cause getChildContext()
    // to be executed.
    modalContainerElement: HTMLElement | null | undefined;
};

const TRANSITION_ANIMATION_DURATION = 300;

class Modal extends React.Component<Props, State> {
    modal: HTMLElement | null | undefined;
    modalAndNavigation: HTMLElement | null | undefined;
    closeTimeout: number | null | undefined;
    startAnimationsTimeout: number | null | undefined;
    isModalOpenTimeout: number | null | undefined;
    willBeVisibleTimeout: number | null | undefined;
    // @ts-expect-error - TS2564 - Property 'isShiftKeyDown' has no initializer and is not definitely assigned in the constructor.
    isShiftKeyDown: boolean;

    static defaultProps: DefaultProps = {
        shouldAnimate: true,
        shouldBackdropDismiss: () => true,
        fromAnotherModal: false,
        visible: true,
        onRequestClose: () => true,
        onClose: () => {},
        preventDismissal: false,
        hideCloseIcon: false,
        restrictFocusToModal: true,
    };

    state: State = {
        willBeVisible: false,
        isModalOpen: false,
        startAnimations: false,
        forceClosingAnimation: false,
        modalContainerElement: undefined,
    };

    getChildContext(): {
        modalContainerElement: HTMLElement | null | undefined;
    } {
        return {
            modalContainerElement: this.state.modalContainerElement,
        };
    }

    // TODO(jangmi, WEB-1514): Remove usage of `UNSAFE_componentWillMount`
    UNSAFE_componentWillMount() {
        document.body?.addEventListener("keydown", this.handleKeyDown);
        document.body?.addEventListener("keyup", this.handleKeyUp);
    }

    componentDidMount() {
        // We want to keep a reference to the element that triggered the modal so that
        // we can return focus to it when the modal closes.
        // However, if the page loaded with the modal already open, there will be no
        // trigger element and instead the document body will be the active element.
        // In this case, we'll track the triggerlessModalFocusAnchor.
        if (document.activeElement === document.body) {
            const triggerlessModalFocusAnchor = document.getElementById(
                TRIGGLESS_MODAL_FOCUS_ANCHOR_ID,
            );
            this.elementThatTriggeredModal = triggerlessModalFocusAnchor;
        } else {
            // If the modal wasn't opened automatically, grab the element that's currently
            // focused at the time that the modal is loading, which should be the trigger.
            // @ts-expect-error - TS2322 - Type 'Element | null' is not assignable to type 'HTMLElement | null | undefined'.
            this.elementThatTriggeredModal = document.activeElement;
        }

        this._isMounted = true;

        document.addEventListener(
            "focusout",
            this._restrictedFocusHandler,
            true,
        );

        // Timeout to ensure transition works on first render
        if (this.props.shouldAnimate) {
            const delay = 100;
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
            // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'number'.
            // eslint-disable-next-line no-restricted-syntax
            this.startAnimationsTimeout = setTimeout(
                () =>
                    this.setState({
                        startAnimations: true,
                    }),
                0,
            );
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
            // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'number'.
            // eslint-disable-next-line no-restricted-syntax
            this.willBeVisibleTimeout = setTimeout(() => {
                this.setState({
                    willBeVisible: true,
                });
                // NOTE(jeff): We need to initiate the very first focus onto the
                // modal.
                this._initialModalFocus();
            }, delay);
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
            // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'number'.
            // eslint-disable-next-line no-restricted-syntax
            this.isModalOpenTimeout = setTimeout(
                () => this.setState({isModalOpen: true}),
                delay + TRANSITION_ANIMATION_DURATION,
            );
        } else {
            // NOTE(jeff): We need to initiate the very first focus onto the
            // modal.
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
            // eslint-disable-next-line no-restricted-syntax
            setTimeout(() => {
                if (this._isMounted) {
                    this.setState(
                        {isModalOpen: true, willBeVisible: true},
                        () => this._initialModalFocus(),
                    );
                }
            }, 0);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;

        document.removeEventListener(
            "focusout",
            this._restrictedFocusHandler,
            true,
        );

        document.body?.removeEventListener("keydown", this.handleKeyDown);
        document.body?.removeEventListener("keyup", this.handleKeyUp);

        if (this.startAnimationsTimeout) {
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
            // eslint-disable-next-line no-restricted-syntax
            clearTimeout(this.startAnimationsTimeout);
        }
        if (this.willBeVisibleTimeout) {
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
            // eslint-disable-next-line no-restricted-syntax
            clearTimeout(this.willBeVisibleTimeout);
        }
        if (this.isModalOpenTimeout) {
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
            // eslint-disable-next-line no-restricted-syntax
            clearTimeout(this.isModalOpenTimeout);
        }
        if (this.closeTimeout) {
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
            // eslint-disable-next-line no-restricted-syntax
            clearTimeout(this.closeTimeout);
        }

        // Clear all the WB schedule timeouts/intervals.
        this.props.schedule.clearAll();
    }

    _isMounted = false;

    /**
     * The element that was active when the modal was opened, which should be the
     * element that triggered the modal's opening. If the modal was opened automatically
     * on pageload, there is no trigger element, so this element will be the
     * "Skip to main content" link because that should be the first element on the page.
     * This logic is used to maintain a logical focus when the modal closes, for
     * accessibility reasons.
     */
    elementThatTriggeredModal: HTMLElement | null | undefined = null;
    /**
     * When focus has been returned to the elementThatTriggeredModal,
     * this variable flips to true so we know it's okay that the user's focus
     * has left the modal's focus trap.
     */
    focusReturnedToTrigger = false;
    _idRoot: string | null | undefined = null;

    getChildrenId(): string | null | undefined {
        if (!this._idRoot) {
            return null;
        }
        return `${this._idRoot}children`;
    }

    getTitleId(): string | null | undefined {
        if (!this._idRoot) {
            return null;
        }
        return `${this._idRoot}title`;
    }

    getCloseButtonId(): string | null | undefined {
        if (!this._idRoot) {
            return null;
        }
        return `${this._idRoot}close`;
    }

    _initialModalFocus: () => void = () => {
        // NOTE(miguel): In some browsers such as safari the event focusout is
        // not triggered when the component is mounted. But in some other browsers
        // such as chrome focusout does trigger when the component mounts... So to
        // keep the behavior consistent across browsers, I am going to do this force
        // focus initialization to normalize the behavior. Otherwise you will see
        // that we dont consistently focus the correct element when a modal first
        // launches in safari.
        const initializeFocus = (e: FocusEvent) => {
            this._restrictedFocusHandler(e);
            document.removeEventListener("focus", initializeFocus, true);
        };

        document.addEventListener("focus", initializeFocus, true);

        // NOTE(jeff): This is called to set focus into the modal on the very
        // first appearance. This will cause the focus handler to fire which
        // will then make all the decisions about what gets focused.
        if (!this.modal || !this.state.willBeVisible) {
            // If we're not visible yet or we don't have a modal, then delay
            // this.
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
            // eslint-disable-next-line no-restricted-syntax
            setTimeout(this._initialModalFocus, 0);
            return;
        }
        this.modal && this.modal.focus();
    };

    maybeScheduleFocusTimeout: (attemptNumber: number) => void = (
        attemptNumber: number,
    ) => {
        if (attemptNumber > MAX_FOCUS_ATTEMPT_NUMBER) {
            // We've tried our best to wait for something in the modal becomes focusable.
            // If we've gotten this far and we're still looping, give up and try to focus
            // on the modal.
            if (this.modal) {
                this.modal.focus();
            }

            Log.log(
                "Unable to manually set focus on an element inside a content modal." +
                    " Falling back to setting focus on the modal element itself.",
            );

            return;
        }

        const {schedule} = this.props;
        // We're okay to schedule a timeout and try to focus on the modal contents
        // again in half a second.
        schedule.timeout(
            () => this.focusFirstElementOrModal(attemptNumber + 1),
            500,
        );
        return;
    };

    _setContainerState: (node: React.ElementRef<any>) => void = (
        node: React.ElementRef<any>,
    ) => {
        // As this is a ref callback, which is updated before componentDidMount,
        // we need to wait until the next frame for `_isMounted` to be true.

        // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
        // eslint-disable-next-line no-restricted-syntax
        setTimeout(() => {
            if (this._isMounted && node !== this.state.modalContainerElement) {
                // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'HTMLElement | null | undefined'.
                this.setState({modalContainerElement: node});
            }
        }, 0);
    };

    firstFocus = true;
    _restrictedFocusHandler: (e: FocusEvent) => void = (e: FocusEvent) => {
        if (!this.props.restrictFocusToModal) {
            return;
        }

        const relatedTarget = e.relatedTarget as Node;
        const isMounted = !!this.modal;
        const isAlreadyFocused = isMounted && this.modal === e.target;

        // If the element that is about to gain focus is outside of the modal
        // then we make sure to redirect focus to an element that is within
        // the modal itself.
        const goingOutOfScope =
            !relatedTarget ||
            (this.state.modalContainerElement &&
                !this.state.modalContainerElement.contains(relatedTarget));

        // In the event that we have multiple modals open, don't try to steal
        // focus from it.
        const targetActsLikeModal = (target?: EventTarget | null) =>
            target instanceof HTMLElement &&
            target.className.indexOf("modal") !== -1;

        const focusedOnOtherModal =
            targetActsLikeModal(e.target) ||
            targetActsLikeModal(e.relatedTarget);

        const redirectFocus =
            isMounted &&
            goingOutOfScope &&
            !isAlreadyFocused &&
            !focusedOnOtherModal &&
            !this.focusReturnedToTrigger;

        if (redirectFocus || this.firstFocus) {
            // If the user tabs enough to escape the modal we try to focus the
            // first or last element, depending on if they are Tab'ing (moving
            // forwards) or Shift+Tab'ing (moving backwards) through the UX. If
            // we can't find a focusable child, we'll focus the modal itself to
            // trap navigation inside the modal.
            if (this.isShiftKeyDown && !this.firstFocus) {
                this.focusLastElementOrModal();
            } else {
                this.focusFirstElementOrModal();
            }
            e.preventDefault();
            this.firstFocus = false;
        }
    };

    getAllFocusableChildren: (
        parentElement?: Element | null | undefined,
    ) => Array<HTMLElement> = (parentElement?: Element | null) => {
        if (!parentElement) {
            return [];
        }

        // querySelectorAll doesn't return an Array instance, but it can be
        // acted on by Array.prototype.filter, so let's call it from the
        // prototype.
        // NOTE(diedra): We are only looking at HTML elements that intended to be focusable
        // (a, button, input, select, textarea) because some systems that I'm too polite
        // to name don't support focusing on divs even if they have a tabindex or 0 or -1.
        // TODO(diedra): add "[tabindex]:not([tabindex='-1']):not([disabled])" to the query list
        // if iPads ever get their act together.
        return Array.prototype.filter.call(
            parentElement.querySelectorAll(
                "button:not([tabindex='-1']):not([disabled]), " +
                    "[href]:not([tabindex='-1']):not([disabled]), " +
                    "input:not([tabindex='-1']):not([disabled]), " +
                    "select:not([tabindex='-1']):not([disabled]), " +
                    "textarea:not([tabindex='-1']):not([disabled])",
            ),
            (e) => !!e.offsetParent,
        );
    };

    focusLastElementOrModal: () => void = () => {
        if (!this.modal) {
            return;
        }
        const focusableChildren = this.getAllFocusableChildren(
            this.modalAndNavigation || this.modal,
        );
        if (focusableChildren.length === 0) {
            this.modal && this.modal.focus();
        } else {
            focusableChildren[focusableChildren.length - 1].focus();
        }
    };

    focusFirstElementOrModal: (attemptNumber?: number) => void = (
        attemptNumber = 1,
    ) => {
        const childrenId = this.getChildrenId();

        if (!this.modal || !childrenId) {
            // If there is no modal or childrenId, wait half a second and try again.
            this.maybeScheduleFocusTimeout(attemptNumber);
            return;
        }
        // If this is the first time we're focusing something in our model,
        // WCAG guidelines say that we should focus the first item in the modal
        // when it shows. Rather than the first, which would be the close
        // button, we're going to focus the first thing inside the main body
        // which should be what the user will be interacting with.
        //
        // If it's not our first focus, we want to loop around ALL
        // interactive parts of the UX, and so we include children of the entire
        // modal and prev/next buttons, not just the content area.
        const focusableChildren = this.getAllFocusableChildren(
            this.firstFocus
                ? this.modal && this.modal.querySelector(`[id="${childrenId}"]`)
                : this.modalAndNavigation || this.modal,
        );
        if (focusableChildren.length === 0) {
            const closeButtonId = this.getCloseButtonId();
            // If there are no focusable children to be found,
            // let's try for the close/back button.
            const maybeCloseButton =
                closeButtonId && document.getElementById(closeButtonId);
            if (maybeCloseButton && maybeCloseButton.focus) {
                maybeCloseButton.focus();
            } else {
                // If there are no focusable children AND no close/back button,
                // that must mean we're trying to focus on the modal when it hasn't
                // finished loading yet.
                this.maybeScheduleFocusTimeout(attemptNumber);
            }
        } else {
            // Sometimes, focusing this causes _restrictedFocusHandler to fire, which
            // causes this to focus, and we bust our call stack.  Don't focus it if it
            // already has focus.
            if (focusableChildren[0] !== document.activeElement) {
                focusableChildren[0].focus();
            }
        }
    };

    handleKeyDown: (e: KeyboardEvent) => void = (e: KeyboardEvent) => {
        if (
            e.keyCode === 27 &&
            !this.props.preventDismissal &&
            // prevent the modal from closing when escape is being used to
            // dismiss the zoomed in image with the keyboard
            // JHead: This is a major hack related to packages/perseus/zoom.js
            // in https://github.com/khan/perseus.
            // which also attaches a document key event listener to close the
            // zoomed in image
            // Since the plan is to remove these modals in 2022 I think a
            // temporary hack is the right solution for us rather than rewriting
            // the perseus component
            !document.querySelector(".zoom-overlay")
        ) {
            this.handleClose();
        }

        if (e.shiftKey) {
            this.isShiftKeyDown = true;
        }
    };

    handleKeyUp: (e: KeyboardEvent) => void = (e: KeyboardEvent) => {
        if (!e.shiftKey) {
            this.isShiftKeyDown = false;
        }
    };

    handleClose: () => void = () => {
        if (this.props.onBack) {
            this.props.onBack();
        } else {
            const result = this.props.onRequestClose();
            if (result !== false) {
                if (this.props.shouldAnimate) {
                    this.setState({forceClosingAnimation: true});
                }
                this.setState({willBeVisible: false});
                // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
                // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'number'.
                // eslint-disable-next-line no-restricted-syntax
                this.closeTimeout = setTimeout(() => {
                    // HACK(jvoll): When we are logged out and open a modal
                    // (quiz/test/exercise) on a unit page we create a phantom
                    // user and show a notification asking them to login (on
                    // mobile-web this is rendered behind the modal and not
                    // visible until the modal is closed). On creation of the
                    // notification, an `initNav` event is dispatched from the
                    // notifications.js code. As far as I can tell, the main
                    // purpose of this event is to tell components to re-measure
                    // themselves since we are now showing a notification at the
                    // top of the page. BECAUSE this happens when a modal is
                    // displaying full screen on mobile web, the measurement
                    // that get's taken inside sticky-module.jsx is way off, it
                    // seems the topBoundElement is scrolled off the page. This
                    // results in sticky-module.jsx (such as the one used for
                    // the header) not knowing when it should be in "sticky"
                    // mode versus "normal" mode. Another _hack_ in
                    // topic-progress.jsx relies on us knowing when the header
                    // is "sticky" versus "normal" so that that component
                    // renders with the correct top offset. THEREFORE, I'm
                    // dispatchign an `initNav` event on modal close so that
                    // sticky-module.jsx knows to re-measure itself and all of
                    // the aforementioned works correctly. Ugggggghh.
                    window.dispatchEvent(new CustomEvent("initNav"));

                    this.props.onClose();
                }, TRANSITION_ANIMATION_DURATION);
            }
        }

        if (this.elementThatTriggeredModal != null) {
            // Return focus to the element that triggered the modal and track that
            // we're doing so so the modal trap doesn't try to grab focus back.
            this.focusReturnedToTrigger = true;
            this.elementThatTriggeredModal.focus();
        }
    };

    renderModalWithIDProvider(): React.ReactNode {
        return (
            <Id>
                {(uniqueId) => {
                    this._idRoot = uniqueId;
                    return this.renderModal();
                }}
            </Id>
        );
    }

    renderModal(): React.ReactNode {
        const {
            shouldAnimate,
            fromAnotherModal,
            navigator,
            preventDismissal,
            hideCloseIcon,
            shouldBackdropDismiss,
            topRightElements,
            useBackIcon,
            visible,
            inline,
            topBar,
            controlButtonColors,
        } = this.props;

        const {
            startAnimations,
            willBeVisible,
            isModalOpen,
            forceClosingAnimation,
        } = this.state;

        const styleProp = Array.isArray(this.props.style)
            ? this.props.style
            : [this.props.style];

        const modalStyles =
            (shouldAnimate && !fromAnotherModal) || forceClosingAnimation
                ? [
                      styles.modal,
                      !isModalOpen && startAnimations && styles.animateInModal,
                      isModalOpen && startAnimations && styles.animateOutModal,
                      !willBeVisible && styles.modalDown,
                      willBeVisible && styles.modalUp,
                      ...styleProp,
                  ]
                : [styles.modal, ...styleProp];

        let children = this.props.children;
        if (typeof children === "function") {
            children = children({
                closeModal: () => this.handleClose(),
            });
        }

        const childrenArray = React.Children.toArray(children);

        const bodyChildren = childrenArray.filter(
            // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'. | TS2339 - Property 'type' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'.
            (child) => child.type !== ModalFooter && child.type !== ModalHeader,
        );

        const headers = childrenArray.filter(
            // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'.
            (child) => child.type === ModalHeader,
        );

        if (headers.length > 1) {
            Log.error(
                "Modal can only display a single header",
                Errors.Internal,
            );
        }

        const header = headers[0];

        const footers = childrenArray.filter(
            // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'.
            (child) => child.type === ModalFooter,
        );

        if (footers.length > 1) {
            Log.error(
                "Modal can only display a single footer",
                Errors.Internal,
            );
        }

        const footer = footers[0];

        // Determine the left and right corners. If we have no header, these
        // will be rendered as direct, absolutely positioned children. If we
        // have a header, their rendering will be delegated to the header, where
        // they'll share space with the title, etc.
        const topLeftCorner = (!preventDismissal || useBackIcon) &&
            !hideCloseIcon && (
                <div className={css(styles.topLeftCorner)}>
                    <DeprecatedControlButton
                        onClick={this.handleClose}
                        padding={12}
                        colors={controlButtonColors}
                        testId="close-modal-btn"
                        id={this.getCloseButtonId()}
                    >
                        {useBackIcon ? (
                            <BackIcon />
                        ) : (
                            <CloseIcon size={24} iconSize={12} />
                        )}
                    </DeprecatedControlButton>
                </div>
            );

        const topRightCorner = topRightElements && (
            <div
                className={css(styles.topRightCorner)}
                data-testid="modal-top-right-element"
            >
                {topRightElements}
            </div>
        );

        let animate: "in" | "out" | null;
        if ((shouldAnimate && !fromAnotherModal) || forceClosingAnimation) {
            if (!isModalOpen && startAnimations) {
                animate = "in";
            } else if (isModalOpen && startAnimations) {
                animate = "out";
            } else {
                animate = null;
            }
        } else {
            animate = null;
        }

        let contents: React.ReactNode = (
            <React.Fragment>
                {!inline && <ScrollDisabler />}

                {topBar && <div className={css(styles.topBar)}>{topBar}</div>}

                <Backdrop
                    inline={inline}
                    willBeVisible={willBeVisible}
                    animate={animate}
                    onClick={() => {
                        if (shouldBackdropDismiss() && !preventDismissal) {
                            this.handleClose();
                        }
                    }}
                />

                {navigator && navigator.hasPrev() && (
                    <button
                        aria-label={t`previous`}
                        data-testid="prev-arrow"
                        className={css(
                            styles.sideNav,
                            RequestInfo.isRTL && styles.sideNavRTL,
                            styles.prev,
                        )}
                        onClick={() =>
                            navigator.hasPrev() && navigator.onPrev()
                        }
                    >
                        <DeprecatedIcon icon={icons.angleLeft} size={17} />
                    </button>
                )}

                <div
                    aria-labelledby={this.getTitleId()}
                    aria-describedby={this.getChildrenId()}
                    className={css(
                        ...modalStyles,
                        inline && styles.inlineModal,
                    )}
                    ref={(elem) => (this.modal = elem)}
                    role={this.props.alert ? "alertdialog" : "dialog"}
                    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'number | undefined'.
                    tabIndex="-1"
                    aria-modal={true}
                >
                    {header &&
                        // @ts-expect-error - TS2769 - No overload matches this call.
                        React.cloneElement(header, {
                            id: this.getTitleId(),
                            topLeftCorner,
                            topRightCorner,
                        })}
                    <div
                        // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | undefined'.
                        id={this.getChildrenId()}
                        className={css(styles.dialogBody)}
                    >
                        {bodyChildren}
                    </div>
                    {footer}
                    {!header && (
                        <div className={css(styles.topLeftCornerInModal)}>
                            {topLeftCorner}
                        </div>
                    )}
                    {!header && (
                        <div className={css(styles.topRightCornerInModal)}>
                            {topRightCorner}
                        </div>
                    )}
                </div>

                {navigator && navigator.hasNext() && (
                    <button
                        aria-label={t`next`}
                        data-testid="next-arrow"
                        className={css(styles.sideNav, styles.next)}
                        onClick={() =>
                            navigator.hasNext() && navigator.onNext()
                        }
                    >
                        <DeprecatedIcon icon={icons.angleRight} size={17} />
                    </button>
                )}
            </React.Fragment>
        );

        if (!inline) {
            contents = (
                <div
                    ref={(elem) => (this.modalAndNavigation = elem)}
                    className={css(styles.innerContainer)}
                    // The wonder-blocks dropdown looks for this attribute on a
                    // parent node to know where to insert the dropdown node.
                    // We need this wrapper because of a bug with Popper.js on
                    // on Firefox, where it chokes when the offsetParent is
                    // position:fixed (see comment on `innerContainer`)
                    data-modal-launcher-portal
                >
                    {contents}
                </div>
            );
        }

        return (
            <div>
                <div
                    ref={this._setContainerState}
                    className={css(
                        styles.container,
                        !visible && styles.hidden,
                        inline && styles.inlineWrap,
                        topBar && styles.withTopBar,
                    )}
                    // The wonder-blocks dropdown looks for this attribute on a
                    // parent node to know where to insert the dropdown node.
                    // See wonder-blocks-modal/util/
                    // maybe-get-portal-mounted-modal-host-element.js
                    data-modal-launcher-portal
                >
                    {contents}
                </div>
                {/* HACK(mdr): We also render an extra "sentinel" node after
                 *     the modal container. This ensures that, even if the
                 *     modal contains the last focusable element in the
                 *     document, pressing tab will keep us in the page (by
                 *     moving focus to here), rather than moving focus out of
                 *     the page. (If we moved focus out of the page, then we
                 *     wouldn't be able to detect the focus event and redirect
                 *     it!)
                 *
                 *     Focus will only be on this element very briefly before
                 *     being redirected, so we don't expect it to disrupt the
                 *     screen reader user experience. (If it does, then our
                 *     entire focus trap strategy has the same problem, since
                 *     the user will _always_ briefly focus another node
                 *     before their focus is redirected back to here.) */}
                {/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- TODO: Address a11y error */}
                {/* @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'number | undefined'. */}
                {this.props.restrictFocusToModal && <div tabIndex="0" />}
                {/* eslint-enable jsx-a11y/no-noninteractive-tabindex -- TODO: Address a11y error */}
            </div>
        );
    }

    render(): React.ReactNode {
        const {hostInBody} = this.props;

        if (hostInBody) {
            return createPortal(
                this.renderModalWithIDProvider(),
                getModalParent(),
            );
        }
        return this.renderModalWithIDProvider();
    }
}

// @ts-expect-error - TS2339 - Property 'childContextTypes' does not exist on type 'typeof Modal'.
Modal.childContextTypes = {
    modalContainerElement: PropTypes.instanceOf(HTMLElement),
};

export const sharedModalStyles = StyleSheet.create({
    // eslint-disable-next-line react-native/no-unused-styles
    subtitle: {
        ...deprecatedTypography.modalSubtitle,
        color: color.offBlack50,
    },
});

const styles = StyleSheet.create({
    inlineWrap: {
        display: "block",
        overflow: "auto",
        position: "relative",
    },
    // Contains both the backdrop and the modal
    container: {
        position: "fixed",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: zindexModal,
    },
    // Contains everything twice because we can't have nice things.
    // This is to workaround a Firefox bug in Popper.js, which
    // wonderblocks-dropdown uses.
    // In Firefox, if the offsetParent is position:fixed, the popover
    // is placed according to page scroll, so it's off screen most of
    // the time. So we add an `innerContainer` right inside of the
    // `container` which is position:absolute, which fixes the bug.
    innerContainer: {
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },
    dialogBody: {
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
        flexGrow: 1,
        justifyContent: "center",
    },
    hidden: {
        visibility: "hidden",
    },
    topBar: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: zindexAboveModal,
    },
    // A wrapper around the modal dialog itself, used for offsetting dynamically
    // if required.
    // eslint-disable-next-line react-native/no-unused-styles
    modalWrapper: {
        position: "fixed",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },
    // The modal dialog itself
    modal: {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        maxHeight: "100%",
        maxWidth: "100%",
        backgroundColor: "white",
        borderRadius: baseBorderRadius,
        [mediaQueries.smOrSmaller]: {
            borderRadius: 0,
        },
        WebkitTapHighlightColor: "rgba(0,0,0,0)",
        // This invisible outline is imperceptible under normal conditions, but it
        // appears as a normal full black outline on Windows high contrast mode so
        // users can see where the modal's edges are.
        outline: "1px solid rgba(0,0,0,0)",
        backgroundClip: "padding-box",
    },
    inlineModal: {
        margin: "10px auto",
        position: "relative",
        width: "75%",
        height: "100%",
    },
    modalDown: {
        transform: "translate(0px, 48px)",
        opacity: 0,
        [mediaQueries.smOrSmaller]: {
            transform: "translate(0vw, 100vh)",
            opacity: 1,
        },
    },
    modalUp: {
        transform: "translate(0px, 0px)",
        opacity: 1,
        [mediaQueries.smOrSmaller]: {
            transform: "translate(0vw, 0vh)",
            opacity: 1,
        },
    },
    animateInModal: {
        transition: [
            `transform ${TRANSITION_ANIMATION_DURATION}ms
                cubic-bezier(0.25, 0.25, 0.25, 1)`,
            `opacity ${TRANSITION_ANIMATION_DURATION}ms
                cubic-bezier(0, 0.25, 0.25, 1)`,
        ].join(", "),
        [mediaQueries.smOrSmaller]: {
            transition: `transform ${TRANSITION_ANIMATION_DURATION}ms
                cubic-bezier(0.25, 0.25, 0.25, 1)`,
        },
    },
    animateOutModal: {
        transition: [
            `transform ${TRANSITION_ANIMATION_DURATION}ms
                cubic-bezier(0.25, 0.25, 0.25, 1)`,
            "opacity 0.2s cubic-bezier(0, 0.25, 0.25, 1)",
        ].join(", "),
        [mediaQueries.smOrSmaller]: {
            transition: `transform ${TRANSITION_ANIMATION_DURATION}ms
                cubic-bezier(0, 0.25, 0.25, 1)`,
        },
    },
    sideNav: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "absolute",
        maxWidth: "5%",
        width: 48,
        top: 0,
        height: "100%",
        color: "white",
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
        background: "none",
        border: "none",
        ":hover": {
            color: color.white,
            opacity: 0.5,
            cursor: "pointer",
        },
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    sideNavRTL: {
        // On right-to-left language versions of the product, the previous/next
        // links are flipped horizontally to the opposite side of the screen.
        transform: "scaleX(-1)",
    },
    prev: {
        left: 0,
    },
    next: {
        right: 0,
    },
    topLeftCorner: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        height: 60,
        paddingLeft: 6,
        zIndex: 1,
    },
    topRightCorner: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        height: 60,
        paddingRight: 6,
        zIndex: 1,
    },
    topLeftCornerInModal: {
        position: "absolute",
        left: 0,
        top: 0,
    },
    topRightCornerInModal: {
        position: "absolute",
        right: 0,
        top: 0,
    },
    withTopBar: {
        top: topBarHeightDesktop,
        [mediaQueries.smOrSmaller]: {
            top: topBarHeightMobile,
        },
    },
});

// TODO(kevinb) move these into reusable-components-package
const icons = {
    angleLeft: {
        path: "M58.622 8.272q0 1.32-1.056 2.376l-39.512 39.512 39.512 39.512q.968.968.968 2.288t-.968 2.288l-5.016 5.104q-.968.968-2.288.968t-2.376-.968l-46.816-46.904q-.968-.968-1.056-2.288 0-1.32 1.056-2.376l46.816-46.816q.968-.968 2.376-.968t2.288.968l5.016 5.016q1.056.968 1.056 2.288z",
        width: 58.421,
        height: 100,
    },
    angleRight: {
        path: "M58.524 50.16q0 1.32-.968 2.288l-46.904 46.904q-.968.968-2.288.968t-2.376-.968l-5.016-5.104q-.968-.968-.968-2.288t.968-2.288l39.512-39.512-39.512-39.512q-.968-.968-.968-2.376t.968-2.288l5.016-5.016q.968-.968 2.376-.968t2.288.968l46.904 46.816q.968 1.056.968 2.376z",
        width: 58.333,
        height: 100,
    },
} as const;

type ExportProps = WithoutActionScheduler<
    JSX.LibraryManagedAttributes<
        typeof Modal,
        React.ComponentProps<typeof Modal>
    >
>;

// NOTE(kevinb): We can't use HOCs for this component since some of its
// consumers make use of ref forwarding.  This is because almost all of
// our custom HOCs will not support ref forwarding in the future because
// there isn't a way to make the types work when wrapping a component in
// multiple HOCs.
export default React.forwardRef<Modal, ExportProps>((props, ref) => {
    return (
        <ActionSchedulerProvider>
            {(schedule) => <Modal {...props} schedule={schedule} ref={ref} />}
        </ActionSchedulerProvider>
    );
});

export {ModalFooter, ModalHeader};
